import React, { useState, useEffect } from "react";
import styled from "styled-components";
import variables from "../../../constants/assets/variables";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import { makeStyles } from "@material-ui/core/styles";
import CancelIcon from "@material-ui/icons/Cancel";
import Menu from "@material-ui/core/Menu";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 120,
    width: "100%",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    padding: "8px 12px",
    minHeight: "40px",
    border: `1px solid ${variables.grey300}`,
    borderRadius: "4px",
    backgroundColor: variables.white,
    cursor: "pointer",
  },
  chip: {
    margin: 2,
  },
  placeholder: {
    color: "#757575",
  },
  menuPaper: {
    maxHeight: 48 * 4.5 + 8,
    width: 250,
  },
  list: {
    padding: 0,
  },
  listItem: {
    "&:hover": {
      backgroundColor: variables.grey100,
    },
  },
}));

const MultiSelectDropdown = ({
  options,
  value = [],
  handleChange,
  keyToChange,
  labelKey = "label",
  valueKey = "key",
  disabled = false,
  placeholder = "Select",
  width = "100%",
  id,
  ...props
}) => {
  const classes = useStyles();
  const [selectedValues, setSelectedValues] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectRef = React.useRef(null);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedValues(value);
    }
  }, [value]);

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (option) => {
    const isAlreadySelected = selectedValues.some((selectedValue) => {
      const selectedValueKey =
        typeof selectedValue === "object"
          ? selectedValue[valueKey]
          : selectedValue;
      const optionValueKey =
        typeof option === "object" ? option[valueKey] : option;
      return selectedValueKey === optionValueKey;
    });

    if (!isAlreadySelected) {
      const newSelectedValues = [...selectedValues, option];
      setSelectedValues(newSelectedValues);

      const selectedKeys = newSelectedValues.map((item) =>
        typeof item === "object" ? item[valueKey] : item
      );

      handleChange(keyToChange, selectedKeys);
    }

    handleClose();
  };

  const handleDelete = (valueToDelete) => {
    const newSelectedValues = selectedValues.filter((selectedValue) => {
      const currentValue =
        typeof selectedValue === "object"
          ? selectedValue[valueKey]
          : selectedValue;
      const deleteValue =
        typeof valueToDelete === "object"
          ? valueToDelete[valueKey]
          : valueToDelete;
      return currentValue !== deleteValue;
    });

    setSelectedValues(newSelectedValues);

    const selectedKeys = newSelectedValues.map((item) =>
      typeof item === "object" ? item[valueKey] : item
    );

    handleChange(keyToChange, selectedKeys);
  };

  const normalizedOptions = options.map((option) => {
    if (typeof option === "string") {
      return { [valueKey]: option, [labelKey]: option };
    }
    return option;
  });

  const normalizedSelectedValues = selectedValues.map((val) => {
    if (typeof val === "string") {
      return (
        options.find((opt) => opt[valueKey] === val || opt === val) || {
          [valueKey]: val,
          [labelKey]: val,
        }
      );
    }
    return val;
  });

  return (
    <StyledContainer style={{ width }}>
      <div
        ref={selectRef}
        className={classes.chips}
        onClick={handleClick}
        data-testid={`${id}-container`}
      >
        {normalizedSelectedValues.length > 0 ? (
          normalizedSelectedValues.map((value) => (
            <Chip
              key={value[valueKey]}
              label={value[labelKey]}
              className={classes.chip}
              onDelete={disabled ? undefined : () => handleDelete(value)}
              deleteIcon={
                <CancelIcon
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                />
              }
            />
          ))
        ) : (
          <span className={classes.placeholder}>{placeholder}</span>
        )}
      </div>

      <Menu
        id={id}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        PaperProps={{
          className: classes.menuPaper,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        getContentAnchorEl={null}
      >
        <List className={classes.list}>
          {normalizedOptions.map((option) => {
            const isSelected = normalizedSelectedValues.some(
              (selectedValue) => selectedValue[valueKey] === option[valueKey]
            );

            if (!isSelected) {
              return (
                <ListItem
                  button
                  key={option[valueKey]}
                  onClick={() => handleItemClick(option)}
                  className={classes.listItem}
                >
                  <ListItemText primary={option[labelKey]} />
                </ListItem>
              );
            }
            return null;
          })}
          {normalizedOptions.filter(
            (option) =>
              !normalizedSelectedValues.some(
                (selectedValue) => selectedValue[valueKey] === option[valueKey]
              )
          ).length === 0 && (
            <ListItem>
              <ListItemText primary="All options selected" />
            </ListItem>
          )}
        </List>
      </Menu>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  margin-bottom: 16px;
`;

export default MultiSelectDropdown;
